import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isInWishlist } from '../util/wishlist/wishlist';
import { toggleInWishlist } from '../ducks/user.duck';
import { createResourceLocatorString } from '../util/routes';
import { useRouteConfiguration } from '../context/routeConfigurationContext';

/**
 * Custom hook to handle wishlist functionality
 * 
 * @param {string} listingId - The ID of the listing to toggle in wishlist
 * @param {number} [initialLikesCount=0] - Initial likes count for the listing
 * @returns {Object} - Wishlist related states and handlers
 */
export const useWishlist = (listingId, initialLikesCount = 0) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [optimisticState, setOptimisticState] = useState(null);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [optimisticLikesCount, setOptimisticLikesCount] = useState(null);
  
  // Get currentUser from Redux store
  const currentUser = useSelector(state => state.user.currentUser);
  const dispatch = useDispatch();
  const history = useHistory();
  const routes = useRouteConfiguration();
  
  // Determine if the listing is in the wishlist
  const isFilled = isInWishlist(currentUser, listingId);
  
  // Use optimistic state if available, otherwise use the computed value
  const isInUserWishlist = optimisticState !== null ? optimisticState : isFilled;
  
  // Use optimistic count if available, otherwise use actual count
  const displayLikesCount = optimisticLikesCount !== null ? optimisticLikesCount : likesCount;
  
  // Reset optimistic states when the actual values change
  useEffect(() => {
    setOptimisticState(null);
  }, [isFilled]);
  
  useEffect(() => {
    setOptimisticLikesCount(null);
  }, [likesCount]);

  // Update likesCount if initialLikesCount changes
  useEffect(() => {
    setLikesCount(initialLikesCount);
  }, [initialLikesCount]);

  const handleToggleWishlist = (e) => {
    // Prevent click from propagating to parent elements (like ListingCard link)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple clicks while processing
    if (isProcessing) {
      return;
    }
    
    // Set processing state and optimistic updates
    setIsProcessing(true);
    setOptimisticState(!isInUserWishlist);
    setOptimisticLikesCount(displayLikesCount + (!isInUserWishlist ? 1 : -1));
    
    // If user is not logged in, handle redirection using React Router
    if (!currentUser) {
      // Save listing ID to localStorage for adding to wishlist after login
      if (typeof window !== 'undefined') {
        const tempWishlist = JSON.parse(localStorage.getItem('tempWishlist') || '[]');
        if (!tempWishlist.includes(listingId)) {
          tempWishlist.push(listingId);
          localStorage.setItem('tempWishlist', JSON.stringify(tempWishlist));
        }
        
        // Get current location for redirect back after login
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;
        
        // Create a state object to pass to the login page
        const state = { from: currentUrl, wishlist: true };
        
        // Redirect to login page using React Router's history
        history.push(createResourceLocatorString('LoginPage', routes, {}, {}), state);
      }
      
      setIsProcessing(false);
      return Promise.resolve(); // Return a resolved promise for non-logged in users
    }
    
    // Dispatch the toggleInWishlist action
    return dispatch(toggleInWishlist(listingId))
      .then(() => {
        // Success - the likes count will be updated from the server
        setIsProcessing(false);
      })
      .catch(() => {
        // Error - revert the optimistic state
        setOptimisticState(null);
        setOptimisticLikesCount(null);
        setIsProcessing(false);
      });
  };

  return {
    isInWishlist: isInUserWishlist,
    isProcessing,
    toggleWishlist: handleToggleWishlist,
    likesCount: displayLikesCount,
  };
};

export default useWishlist; 